import { DatePicker, Select } from "antd";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { FormikErrors, FormikProps, FormikTouched } from "formik";
import { TFunction } from "i18next";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  IRuleCondition,
  IWorkflowRule,
  IWorkflowsSelectOption,
} from "../../../../../../../@types/Worflows";
import { ArrowDown } from "../../../../../../../assets/icons/ArrowDown";
import { IOption } from "../../../../../../../components/SelectDropdown";
import { TextField } from "../../../../../../../components/TextField";
import { datePickerPanelCss } from "../../../../../../../styles";
import { getWorkflowOptionLabel } from "../../../../../../../utils/workflowOptionLabels";
import { ErrorMessage, selectCss } from "../../../WorkflowsBuilder.styles";
import {
  DatePickerCss,
  HintText,
  SelectContainer,
  TextFieldCss,
  popupOptionsCss,
} from "../RuleComponent.styles";

dayjs.extend(utc);
dayjs.extend(timezone);

const SPECIFIC_DATE = "SpecificDate";
const VIDEO_APPOINTMENT = "VideoAppointment";
const TARGET_DATE_FORMAT = "YYYY-MM-DD";

type Timing = "Before" | "On" | "After";

// EN-5191: built from `t` because the labels are rendered. `value` stays the identity that
// timingFromOffset and setOffset match on, so translating a label changes nothing but the text.
const buildTimingOptions = (t: TFunction): IOption[] => [
  { label: t("workflows_rule_before"), value: "Before", key: "Before" },
  { label: t("workflows_rule_onTheDay"), value: "On", key: "On" },
  { label: t("workflows_rule_after"), value: "After", key: "After" },
];

/** Only events known in advance (video appointments) can be acted on before or on the day. */
function allowsBeforeOrOn(dateEventType?: string): boolean {
  return dateEventType === VIDEO_APPOINTMENT;
}

function timingFromOffset(daysOffset?: number): Timing | undefined {
  if (daysOffset === undefined || daysOffset === null) return undefined;
  if (daysOffset < 0) return "Before";
  if (daysOffset === 0) return "On";
  return "After";
}

function toOffset(timing: Timing, days: number | undefined) {
  if (timing === "On") return 0;
  if (days === undefined || Number.isNaN(days)) {
    return undefined;
  }
  return timing === "Before" ? -Math.abs(days) : Math.abs(days);
}

interface IDateEventFieldsProps {
  formik: FormikProps<IWorkflowRule>;
  dateEventTypes?: IWorkflowsSelectOption[];
  hospitalTimezone?: string;
}

export function DateEventFields({
  formik,
  dateEventTypes,
  hospitalTimezone,
}: IDateEventFieldsProps) {
  const { t } = useTranslation();
  const timingOptions = useMemo(() => buildTimingOptions(t), [t]);

  const condition = formik.values.conditions[0];
  const isSpecificDate = condition?.dateEventType === SPECIFIC_DATE;
  const canPickTiming = allowsBeforeOrOn(condition?.dateEventType);

  // Before/On/After and the day count are UI state; only the signed daysOffset is stored.
  // "After" is the default for every event, so typing a day count alone always yields an offset —
  // a video appointment can still be switched to Before / On the day.
  const [timing, setTiming] = useState<Timing>(
    timingFromOffset(condition?.daysOffset) ?? "After"
  );
  const [days, setDays] = useState<number | undefined>(
    condition?.daysOffset !== undefined && condition?.daysOffset !== null
      ? Math.abs(condition.daysOffset)
      : undefined
  );

  // Formik owns daysOffset; the two controls above are only its Before/After + magnitude reading.
  // When the stored offset changes underneath them (the node re-mounts with other content, the
  // rule loads after first paint) re-read it, so they never show a value the form no longer holds.
  // An undefined offset is the in-between state of a user mid-edit and is left alone.
  const storedOffset = condition?.daysOffset;
  useEffect(() => {
    if (storedOffset === undefined || storedOffset === null) return;
    const storedTiming = timingFromOffset(storedOffset) ?? "After";
    setTiming((current) => (current === storedTiming ? current : storedTiming));
    if (storedTiming !== "On") {
      const storedDays = Math.abs(storedOffset);
      setDays((current) => (current === storedDays ? current : storedDays));
    }
  }, [storedOffset]);

  // The API validates the date against hospital-local "today" and refuses to publish a date already
  // behind it, so the picker disables the same days rather than the browser's.
  const hospitalToday = (
    hospitalTimezone ? dayjs().tz(hospitalTimezone) : dayjs()
  ).format(TARGET_DATE_FORMAT);

  const shouldShowConditionError = (field: keyof IRuleCondition): string => {
    const touchedArray = formik.touched.conditions as
      | FormikTouched<IRuleCondition>[]
      | undefined;
    const errorArray = formik.errors.conditions as
      | FormikErrors<IRuleCondition>[]
      | undefined;

    if (touchedArray && errorArray) {
      const touched = touchedArray[0];
      const error = errorArray[0];

      return touched?.[field] && error?.[field] ? (error[field] as string) : "";
    }

    return "";
  };

  const eventOptions: IOption[] = useMemo(
    () =>
      dateEventTypes?.map((type) => ({
        label: getWorkflowOptionLabel("dateEvent", type, t),
        value: type.type,
        key: type.type,
      })) ?? [],
    [dateEventTypes, t]
  );

  const setOffset = (nextTiming: Timing, nextDays: number | undefined) => {
    formik.setFieldValue(
      "conditions[0].daysOffset",
      toOffset(nextTiming, nextDays)
    );
  };

  // Only the timing and day-count controls mark the offset touched: picking an event
  // is not yet a statement about the days, so it must not surface "Days is required".
  const touchOffset = () =>
    formik.setFieldTouched("conditions[0].daysOffset", true, false);

  const handleEventChange = (value: string) => {
    formik.setFieldValue("conditions[0].dateEventType", value);
    formik.setFieldValue("conditions[0].targetDate", undefined);

    // Past-only events can only be "after"; keep the day count the user typed
    const nextTiming = allowsBeforeOrOn(value) ? timing : "After";
    setTiming(nextTiming);
    if (value === SPECIFIC_DATE) {
      formik.setFieldValue("conditions[0].daysOffset", undefined);
    } else {
      setOffset(nextTiming, days);
    }
  };

  const handleTimingChange = (value: Timing) => {
    setTiming(value);
    setOffset(value, days);
    touchOffset();
  };

  const handleDaysChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const parsed =
      event.target.value === "" ? undefined : Number(event.target.value);
    setDays(parsed);
    setOffset(timing, parsed);
    touchOffset();
  };

  const daysLabel = canPickTiming
    ? t("workflows_rule_days")
    : t("workflows_rule_daysAfter");

  return (
    <>
      <SelectContainer
        isError={shouldShowConditionError("dateEventType") !== ""}
      >
        <p>{t("workflows_rule_event")}</p>
        <Select
          placeholder={t("workflows_rule_selectEvent")}
          value={condition?.dateEventType}
          suffixIcon={<ArrowDown />}
          options={eventOptions}
          popupClassName={popupOptionsCss()}
          notFoundContent={t("workflows_rule_noEvents")}
          className={`${selectCss()} nopan nodrag`}
          onChange={handleEventChange}
        />
        <ErrorMessage>{shouldShowConditionError("dateEventType")}</ErrorMessage>
      </SelectContainer>

      {isSpecificDate && (
        <SelectContainer
          isError={shouldShowConditionError("targetDate") !== ""}
        >
          <p>{t("workflows_rule_date")}</p>
          <DatePicker
            value={
              condition?.targetDate
                ? dayjs(condition.targetDate, TARGET_DATE_FORMAT)
                : undefined
            }
            format="DD/MM/YYYY"
            placeholder={t("workflows_rule_selectDate")}
            suffixIcon={<ArrowDown />}
            disabledDate={(date) =>
              date.format(TARGET_DATE_FORMAT) < hospitalToday
            }
            // EN-3907 (review): the calendar rendered in antd's default blue — popupOptionsCss
            // styles Select options, not a date panel, so nothing in here was themed. The
            // shared panel theme carries the tenant's own colours and Open Sans.
            popupClassName={`${datePickerPanelCss()} ${popupOptionsCss()}`}
            className={`${DatePickerCss()} nopan nodrag`}
            onChange={(date) => {
              formik.setFieldValue(
                "conditions[0].targetDate",
                date ? date.format(TARGET_DATE_FORMAT) : undefined
              );
              formik.setFieldTouched("conditions[0].targetDate", true, false);
            }}
          />
          <ErrorMessage>{shouldShowConditionError("targetDate")}</ErrorMessage>
        </SelectContainer>
      )}

      {condition?.dateEventType && !isSpecificDate && (
        <>
          {canPickTiming && (
            <SelectContainer>
              <p>{t("workflows_rule_timing")}</p>
              <Select
                placeholder={t("workflows_rule_timingPlaceholder")}
                value={timing}
                suffixIcon={<ArrowDown />}
                options={timingOptions}
                popupClassName={popupOptionsCss()}
                className={`${selectCss()} nopan nodrag`}
                onChange={handleTimingChange}
              />
            </SelectContainer>
          )}

          {timing !== "On" && (
            <SelectContainer
              isError={shouldShowConditionError("daysOffset") !== ""}
            >
              <p>{daysLabel}</p>
              <TextField
                name="dateEventDays"
                placeholder="1"
                value={days ?? ""}
                label=""
                onChange={handleDaysChange}
                backgroudColor="white"
                className={`${TextFieldCss()} nopan nodrag`}
                type="number"
              />
              <ErrorMessage>
                {shouldShowConditionError("daysOffset")}
              </ErrorMessage>
            </SelectContainer>
          )}
        </>
      )}

      <HintText>
        {t("workflows_rule_dateEventHint", {
          timezone: hospitalTimezone
            ? `(${hospitalTimezone})`
            : t("workflows_rule_hospitalTime"),
        })}
      </HintText>
    </>
  );
}
